const { debounce } = window.BAO.Utils
const KeenSlider = window.KeenSlider

const { setInitialised, useArrows } = window.BAO.Carousel.plugins

class CollectionSwitcher extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.carousels = new Map()
    this.setupActiveCarousel()
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    this.destroyCarousels()
  }

  setupListeners () {
    super.setupListeners()

    this.handleClick = this.handleClick.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)

    this.listeners.add(this, 'click', this.handleClick)
    this.listeners.add(this, 'keydown', this.handleKeydown)

    this.listeners.add(window, 'resize', debounce(() => {
      this.handleResize()
    }, 500))
  }

  destroyCarousels () {
    this.carousels.forEach((carousel) => {
      carousel.destroy()
    })

    this.carousels.clear()
  }

  handleResize () {
    this.carousels.forEach((carousel) => {
      carousel.update()
    })
  }

  getPanelEls (panel) {
    return this.getElements(
      ['slides', 'slide', 'next', 'previous'],
      this._elIdentifier,
      { context: panel },
    )
  }

  getActivePanel () {
    return this.els?.panel?.elements?.find((panel) => !panel.hidden)
  }

  setupActiveCarousel () {
    const activePanel = this.getActivePanel()

    if (!activePanel) return

    this.setupCarousel(activePanel)
  }

  /**
   * 
   * @param {HTMLElement} panel
   */
  setupCarousel (panel) {
    const tabId = panel.dataset.tabId

    if (this.carousels.has(tabId)) {
      this.carousels.get(tabId).update()
      return
    }

    const panelEls = this.getPanelEls(panel)

    if (!panelEls.slides?.exists || !panelEls.slide?.exists) return

    const slidesEl = panelEls.slides.element
    const carouselSpacing = parseInt(slidesEl.dataset.carouselSpacing || '0', 10)

    const carousel = new KeenSlider(
      slidesEl,
      {
        selector: panelEls.slide.selector,
        loop: false,
        mode: 'snap',
        slides: {
          perView: 1.1825,
          spacing: carouselSpacing,
        },
        breakpoints: {
          '(min-width: 768px)': {
            slides: {
              perView: 2.2,
              spacing: carouselSpacing,
            },
          },
          '(min-width: 1024px)': {
            slides: {
              perView: 3,
              spacing: carouselSpacing,
            },
          },
        },
      },
      [
        setInitialised(),
        useArrows({
          next: panelEls.next?.element,
          previous: panelEls.previous?.element,
        }),
      ],
    )

    this.carousels.set(tabId, carousel)
  }

  handleClick (event) {
    const tabTrigger = event.target.closest(this.els?.tab?.selector)

    if (!tabTrigger) return

    this.switchTab(tabTrigger)
  }

  handleKeydown (event) {
    const tabTrigger = event.target.closest(this.els?.tab?.selector)

    if (!tabTrigger) return

    const tabs = this.els?.tab?.elements || []
    const currentIndex = tabs.indexOf(tabTrigger)
    let nextIndex = null

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length
        break
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
        break
      case 'Home':
        nextIndex = 0
        break
      case 'End':
        nextIndex = tabs.length - 1
        break
      default:
        return
    }

    event.preventDefault()

    const nextTab = tabs[nextIndex]

    this.switchTab(nextTab)
    nextTab.focus()
  }

  switchTab (tabTrigger) {
    const tabId = tabTrigger.dataset.tabId

    this.els.tab?.elements?.forEach((tab) => {
      const isActive = tab.dataset.tabId === tabId

      tab.classList.toggle('is-active', isActive)
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false')
      tab.setAttribute('tabindex', isActive ? '0' : '-1')
    })

    this.els.panel?.elements?.forEach((panel) => {
      const isActive = panel.dataset.tabId === tabId

      panel.classList.toggle('is-active', isActive)

      if (isActive) {
        panel.removeAttribute('hidden')
        this.setupCarousel(panel)
      } else {
        panel.setAttribute('hidden', '')
      }
    })
  }
}

if (!customElements.get('collection-switcher')) {
  customElements.define('collection-switcher', CollectionSwitcher)
}
